import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import { createBlackListClient, getBlackListRecordById, updateBlackListRecord } from "api/blackListClient";
import Checkbox from "components/checkbox";
import DivLoader from "components/divloader/DivLoader";
import InputField from "components/fields/InputField";
import React, { useEffect } from "react";
import { toast } from "react-hot-toast";
import { BsPlusCircle } from "react-icons/bs";
import { MdEdit, MdHighlightOff, MdInfo } from "react-icons/md";
import _ from "lodash";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow } from "@chakra-ui/react";

export interface iCreateOrgForm {
  first_name: string; last_name: string; bin: string;
  last_four: string; email: string;
  is_binAndlast_four?: any; is_client_name?: any; is_email?: any;
}

const CreateBlockClientsModal = ({
  fetchBlackListClientsData, id, info, roleData, infoData,
}: {
  fetchBlackListClientsData: () => void;
  id?: string; info?: boolean; roleData?: any; infoData?: any;
}) => {
  const rootForm = { first_name: "", last_name: "", bin: "", last_four: "", email: "", is_binAndlast_four: false, is_client_name: false, is_email: false };
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = React.useState<boolean>();
  const [isDataLoading, setIsDataLoading] = React.useState<boolean>();
  const [formValues, setFormValues] = React.useState<any>(rootForm);
  const [initialValues, setInitialFormValues] = React.useState<any>();
  const [viewMode, setViewMode] = React.useState<boolean>(info);
  const [formValuesErr, setFormValuesErr] = React.useState<any>(rootForm);
  const [isCloseModal, setIsCloseModal] = React.useState<boolean>(false);
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const [popoverOpenE, setPopoverOpenE] = React.useState(false);

  const handleClose = () => {
    setFormValues(rootForm);
    setFormValuesErr(rootForm);
    setPopoverOpenE(false)
    setPopoverOpen(false)
    setIsCloseModal(true);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  };

  function handleValueChange(e: any) {
    const { id: name, value } = e.target;
    setFormValues((p: any) => ({ ...p, [name]: value }));
    setFormValuesErr((p: any) => ({ ...p, [name]: "" }));
  }

  function validateData() {
    let isValid_bin = true, isValid_first = true, isValid_email = true;
    if (formValues?.is_binAndlast_four) {
      if (formValues?.bin?.length !== 6) {
        setFormValuesErr((p: any) => ({ ...p, bin: "Please enter 6 digit BIN number" }));
        isValid_bin = false;
      }
    }
    if (formValues?.is_client_name && !formValues?.first_name) {
      setFormValuesErr((p: any) => ({ ...p, first_name: "Please enter first name." }));
      isValid_first = false;
    }
    if (formValues?.is_email) {
      const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!regex.test(formValues?.email)) {
        setFormValuesErr((p: any) => ({ ...p, email: "Please enter a valid email address." }));
        isValid_email = false;
      }
    }
    if (!formValues?.is_email && !formValues?.is_binAndlast_four) {
      toast.error("Please provide either card details or email address.");
      return false;
    }
    return isValid_bin && isValid_first && isValid_email;
  }

  async function handleSubmit(e: any) {
    e.preventDefault();
    if (!validateData()) return;
    setIsLoading(true);
    try {
      if (id) {
        const payload = {
          id, bin: formValues?.is_binAndlast_four ? formValues?.bin : "",
          last_four: formValues?.is_binAndlast_four ? formValues?.last_four : "",
          first_name: formValues?.is_client_name ? formValues?.first_name : "",
          last_name: formValues?.is_client_name ? formValues?.last_name : "",
          email: formValues?.is_email ? formValues?.email : "",
        };
        if (!_.isEqual(payload, initialValues)) {
          await updateBlackListRecord(payload);
          toast.success("Blacklist record updated successfully");
          fetchBlackListClientsData();
          handleClose();
        } else {
          toast.error("No changes detected.");
          handleClose();
        }
      } else {
        await createBlackListClient({
          bin: formValues?.is_binAndlast_four ? formValues?.bin : "",
          last_four: formValues?.is_binAndlast_four ? formValues?.last_four : "",
          first_name: formValues?.is_client_name ? formValues?.first_name : "",
          last_name: formValues?.is_client_name ? formValues?.last_name : "",
          email: formValues?.is_email ? formValues?.email : "",
        });
        toast.success("Blacklist record added successfully");
        fetchBlackListClientsData();
        handleClose();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Unable to save record");
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchBlockedUserById(id: string) {
    setIsDataLoading(true);
    await getBlackListRecordById(id)
      .then((data: any) => {
        setFormValues({
          first_name: data[0]?.first_name, last_name: data[0]?.last_name,
          bin: data[0]?.bin, last_four: data[0]?.last_four, email: data[0]?.email,
          is_binAndlast_four: !!data[0]?.bin, is_client_name: !!data[0]?.first_name, is_email: !!data[0]?.email,
        });
        setInitialFormValues({ id, bin: data[0]?.bin, last_four: data[0]?.last_four, first_name: data[0]?.first_name, last_name: data[0]?.last_name, email: data[0]?.email });
      })
      .catch((err: any) => toast.error(err?.response?.data?.message ?? "Unable to fetch data"))
      .finally(() => setIsDataLoading(false));
  }

  const handleOpen = () => {
    if (id) fetchBlockedUserById(id);
    onOpen();
  };

  const panelTitle = viewMode
    ? "View Blacklist Record"
    : id ? "Edit Blacklist Record"
    : "Add to Blacklist";

  return (
    <>
      {info ? (
        roleData?.[0]?.blacklist?.value?.view_blacklist && (
          <Popover trigger="hover" placement="bottom" closeOnBlur  closeOnEsc isOpen={popoverOpen && !isOpen} onOpen={() => setPopoverOpen(true)} onClose={() => setPopoverOpen(false)}>
            <PopoverTrigger>
              <button className="bl-action-icon bl-action-icon--blue" onClick={handleOpen} aria-label="View" onMouseEnter={() => !isOpen && setPopoverOpen(true)} onMouseLeave={() => setPopoverOpen(false)}>
                <MdInfo size={15} />
              </button>
            </PopoverTrigger>
            <PopoverContent border="1px solid #bfdbfe" borderRadius="12px" bg="#eff6ff" maxW="160px" p="0" overflow="hidden" zIndex="20">
              <PopoverHeader bg="#dbeafe" fontSize="12px" fontWeight="700" color="#1e40af" borderBottom="1px solid #bfdbfe" px="12px" py="9px">View Record</PopoverHeader>
              <PopoverArrow bg="#eff6ff" />
            </PopoverContent>
          </Popover>
        )
      ) : id ? (
        <Popover trigger="hover" placement="bottom" closeOnBlur  closeOnEsc isOpen={popoverOpenE && !isOpen} onOpen={() => setPopoverOpenE(true)} onClose={() => setPopoverOpenE(false)}>
          <PopoverTrigger>
            <button className="bl-action-icon bl-action-icon--amber" onClick={handleOpen} aria-label="Edit" onMouseEnter={() => !isOpen && setPopoverOpenE(true)} onMouseLeave={() => setPopoverOpenE(false)}>
              <MdEdit size={15} />
            </button>
          </PopoverTrigger>
          <PopoverContent border="1px solid #fde68a" borderRadius="12px" bg="#fffbeb" maxW="160px" p="0" overflow="hidden" zIndex="20">
            <PopoverHeader bg="#fef3c7" fontSize="12px" fontWeight="700" color="#92400e" borderBottom="1px solid #fde68a" px="12px" py="9px">Edit Record</PopoverHeader>
            <PopoverArrow bg="#fffbeb" />
          </PopoverContent>
        </Popover>
      ) : (
        <button className="bl-add-btn" onClick={handleOpen}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
          Add to Blacklist
        </button>
      )}

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />
        <div className={`bl-panel${isOpen && !isCloseModal ? " bl-panel--open" : " bl-panel--close"}`}>
          <div className="bl-panel-glow" />

          {/* Header */}
          <div className="bl-panel-header">
            <div className="bl-panel-brand">
              <div className="bl-panel-logo">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                </svg>
              </div>
              <div>
                <h2 className="bl-panel-title">{panelTitle}</h2>
                <p className="bl-panel-sub">{viewMode ? "Read-only view" : id ? "Update blacklist record" : "Block a card, name or email"}</p>
              </div>
            </div>
            <button className="bl-panel-close" onClick={handleClose}><MdHighlightOff size={22} /></button>
          </div>

          {/* Body */}
          <div className="bl-panel-body">
            {isDataLoading ? (
              <div className="bl-panel-loading"><div className="bl-spinner-sm" /><p>Loading record…</p></div>
            ) : (
              <>
                {/* Note */}
                <div className="bl-note">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  You must provide either card details or an email address.
                </div>

                {/* Card details section */}
                <div className="bl-form-section">
                  <div className="bl-section-toggle">
                    <Checkbox
                      id="is_binAndlast_four" value={!formValues?.is_binAndlast_four}
                      checked={formValues?.is_binAndlast_four} disabled={viewMode}
                      onChange={(e: any) => setFormValues((p: any) => ({ ...p, is_binAndlast_four: e.target?.value === "false" ? false : e.target?.value === "true" ? true : false }))}
                    />
                    <div className="bl-section-icon bl-section-icon--gray">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                      </svg>
                    </div>
                    <label className="bl-section-label" htmlFor="is_binAndlast_four">Card Details (BIN + Last 4)</label>
                  </div>
                  {formValues?.is_binAndlast_four && (
                    <div className="bl-fields-row">
                      <div className="bl-field">
                        <InputField variant="auth" extra="w-full" label="BIN *" placeholder="e.g. 546470" id="bin" type="number"
                          disabled={viewMode} value={formValues?.bin}
                          state={formValuesErr?.bin ? "error" : ""}
                          onChange={(e) => { const v = e.target?.value; if (v?.length <= 6) { setFormValues((p: any) => ({ ...p, bin: v })); setFormValuesErr((p: any) => ({ ...p, bin: "" })); } }}
                        />
                        {formValuesErr?.bin && <p className="bl-field-err">{formValuesErr.bin}</p>}
                      </div>
                      <div className="bl-field">
                        <InputField variant="auth" extra="w-full" label="Last 4 Numbers" placeholder="e.g. 9876" id="last_four" type="number"
                          disabled={viewMode} value={formValues?.last_four}
                          state={formValuesErr?.last_four ? "error" : ""}
                          onChange={(e) => { const v = e.target.value; if (/^\d{0,4}$/.test(v)) { setFormValues((p: any) => ({ ...p, last_four: v })); setFormValuesErr((p: any) => ({ ...p, last_four: v.length === 4 ? "" : "Must be exactly 4 digits" })); } }}
                        />
                        {formValuesErr?.last_four && <p className="bl-field-err">{formValuesErr.last_four}</p>}
                      </div>
                    </div>
                  )}
                </div>

                {/* Name section */}
                <div className="bl-form-section">
                  <div className="bl-section-toggle">
                    <Checkbox
                      id="is_client_name" value={!formValues?.is_client_name}
                      checked={formValues?.is_client_name} disabled={viewMode}
                      onChange={(e: any) => setFormValues((p: any) => ({ ...p, is_client_name: e.target?.value === "false" ? false : e.target?.value === "true" ? true : false }))}
                    />
                    <div className="bl-section-icon bl-section-icon--gray">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                      </svg>
                    </div>
                    <label className="bl-section-label" htmlFor="is_client_name">Client Name</label>
                  </div>
                  {formValues?.is_client_name && (
                    <div className="bl-fields-row">
                      <div className="bl-field">
                        <InputField variant="auth" extra="w-full" label="First Name" placeholder="Enter first name" id="first_name" type="text" disabled={viewMode} value={formValues?.first_name} state={formValuesErr?.first_name ? "error" : ""} onChange={handleValueChange} />
                        {formValuesErr?.first_name && <p className="bl-field-err">{formValuesErr.first_name}</p>}
                      </div>
                      <div className="bl-field">
                        <InputField variant="auth" extra="w-full" label="Last Name" placeholder="Enter last name" id="last_name" type="text" disabled={viewMode} value={formValues?.last_name} state={formValuesErr?.last_name ? "error" : ""} onChange={handleValueChange} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Email section */}
                <div className="bl-form-section">
                  <div className="bl-section-toggle">
                    <Checkbox
                      id="is_email" value={!formValues?.is_email}
                      checked={formValues?.is_email} disabled={viewMode}
                      onChange={(e: any) => setFormValues((p: any) => ({ ...p, is_email: e.target?.value === "false" ? false : e.target?.value === "true" ? true : false }))}
                    />
                    <div className="bl-section-icon bl-section-icon--gray">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </div>
                    <label className="bl-section-label" htmlFor="is_email">Email Address</label>
                  </div>
                  {formValues?.is_email && (
                    <div className="bl-fields-row">
                      <div className="bl-field">
                        <InputField variant="auth" extra="w-full" label="Email *" placeholder="example@domain.com" id="email" type="text" disabled={viewMode} value={formValues?.email} state={formValuesErr?.email ? "error" : ""} onChange={handleValueChange} />
                        {formValuesErr?.email && <p className="bl-field-err">{formValuesErr.email}</p>}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bl-panel-footer">
            <button className="bl-btn-cancel" onClick={handleClose}>Cancel</button>
            {!viewMode && (
              <button className="bl-btn-submit" onClick={handleSubmit} disabled={!!isLoading}>
                {isLoading ? <span className="bl-spinner-btn" /> : (
                  <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>{id ? "Update" : "Add to Blacklist"}</>
                )}
              </button>
            )}
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .bl-add-btn { display:inline-flex; align-items:center; gap:7px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:700; color:#fff; padding:9px 18px; border-radius:10px; border:none; background:linear-gradient(135deg,#9f1239,#e11d48); cursor:pointer; box-shadow:0 3px 12px rgba(225,29,72,.28); transition:all .15s; }
        .bl-add-btn:hover { transform:translateY(-1px); box-shadow:0 6px 20px rgba(225,29,72,.36); }

        .bl-action-icon { width:30px; height:30px; border-radius:8px; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all .15s; border:1.5px solid; background:none; }
        .bl-action-icon--blue { background:#eff6ff; border-color:#bfdbfe; color:#2563eb; }
        .bl-action-icon--blue:hover { background:#dbeafe; }
        .bl-action-icon--amber { background:#fffbeb; border-color:#fde68a; color:#d97706; }
        .bl-action-icon--amber:hover { background:#fef3c7; }

        .bl-panel { font-family:'Plus Jakarta Sans',sans-serif; position:fixed; top:0; right:0; bottom:0; width:100%; max-width:580px; background:#fff; z-index:1004; display:flex; flex-direction:column; box-shadow:-12px 0 60px rgba(15,23,42,.18); transition:transform .35s cubic-bezier(0.16,1,0.3,1);  }
        .bl-panel--open  { transform:translateX(0); }
        .bl-panel--close { transform:translateX(100%); }

        .bl-panel-glow { height:3px; flex-shrink:0; background:linear-gradient(90deg,#9f1239,#e11d48,#f43f5e,#e11d48,#9f1239); background-size:200% 100%; animation:blGlow 3s linear infinite; }
        @keyframes blGlow { 0%{background-position:0%} 100%{background-position:200%} }

        .bl-panel-header { padding:15px 26px; background:linear-gradient(135deg,#881337 0%,#be123c 60%,#e11d48 100%); display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
        .bl-panel-brand { display:flex; align-items:center; gap:13px; }
        .bl-panel-logo { width:42px; height:42px; border-radius:12px; background:rgba(255,255,255,.15); border:1.5px solid rgba(255,255,255,.25); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .bl-panel-title { font-size:16px; font-weight:800; color:#fff; letter-spacing:-.3px; margin-bottom:2px; line-height:1; }
        .bl-panel-sub { font-size:12px; color:rgba(255,255,255,.55); font-weight:500; }
        .bl-panel-close { background:none; border:none; cursor:pointer; color:rgba(255,255,255,.65); display:flex; align-items:center; transition:color .15s; }
        .bl-panel-close:hover { color:#fff; }

        .bl-panel-body { flex:1; overflow-y:auto; padding:22px 26px; display:flex; flex-direction:column; gap:14px; }
        .bl-panel-body::-webkit-scrollbar { width:4px; }
        .bl-panel-body::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:4px; }

        .bl-panel-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:40px; gap:10px; font-size:13px; color:#94a3b8; }
        .bl-spinner-sm { width:22px; height:22px; border:2.5px solid #e2e8f0; border-top-color:#e11d48; border-radius:50%; animation:blGlow1 .7s linear infinite; }
        @keyframes blGlow1 { to{transform:rotate(360deg)} }

        .bl-note { display:flex; align-items:flex-start; gap:8px; padding:12px 14px; background:linear-gradient(135deg,#fff7ed,#ffedd5); border:1.5px solid #fed7aa; border-radius:11px; font-size:12.5px; font-weight:600; color:#c2410c; }

        .bl-form-section { background:#f8fafc; border:1.5px solid #e2e8f0; border-radius:13px; }
        .bl-section-toggle { display:flex; align-items:center; gap:10px; padding:13px 16px; }
        .bl-section-icon { width:28px; height:28px; border-radius:7px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .bl-section-icon--gray { background:#f1f5f9; border:1.5px solid #e2e8f0; color:#64748b; }
        .bl-section-label { font-size:13px; font-weight:700; color:#374151; cursor:pointer; }
        .bl-fields-row { display:grid; grid-template-columns:1fr 1fr; gap:12px; padding:0 16px 16px; }
        .bl-field { display:flex; flex-direction:column; gap:4px; }
        .bl-field-err { font-size:11.5px; color:#dc2626; font-weight:500; }

        .bl-panel-footer { padding:14px 26px; border-top:1.5px solid #f1f5f9; background:#fafbfc; display:flex; align-items:center; justify-content:flex-end; gap:10px; flex-shrink:0; }
        .bl-btn-cancel { font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:600; color:#64748b; padding:9px 16px; border-radius:10px; border:1.5px solid #e2e8f0; background:#fff; cursor:pointer; transition:all .15s; }
        .bl-btn-cancel:hover { background:#f1f5f9; }
        .bl-btn-submit { display:inline-flex; align-items:center; gap:7px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13.5px; font-weight:700; color:#fff; padding:10px 20px; border-radius:10px; border:none; background:linear-gradient(135deg,#9f1239,#e11d48); cursor:pointer; box-shadow:0 4px 14px rgba(225,29,72,.28); transition:all .15s; }
        .bl-btn-submit:hover { transform:translateY(-1px); box-shadow:0 8px 24px rgba(225,29,72,.36); }
        .bl-btn-submit:disabled { opacity:.5; cursor:not-allowed; transform:none; }
        .bl-spinner-btn { display:inline-block; width:14px; height:14px; border:2px solid rgba(255,255,255,.35); border-top-color:#fff; border-radius:50%; animation:blGlow1 .65s linear infinite; }

        @media (max-width:640px) { .bl-panel { max-width:100%; } .bl-fields-row { grid-template-columns:1fr; } }
      `}</style>
    </>
  );
};

export default CreateBlockClientsModal;